import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isRefreshingRef = useRef(false);

  const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

  const parseTokenAndSetSession = useCallback((jwtToken, refreshToken = null) => {
    try {
      const decoded = jwtDecode(jwtToken);
      const sessionUser = {
        id: decoded.operatorId,
        username: decoded.unique_name || decoded.name || decoded.sub,
        role: decoded.role,
        brandId: decoded.brandId || null
      };
      setToken(jwtToken);
      setUser(sessionUser);
      localStorage.setItem('interaktia_token', jwtToken);
      if (refreshToken) {
        localStorage.setItem('interaktia_refresh_token', refreshToken);
      }
      return sessionUser;
    } catch (err) {
      console.error("Invalid token parsing", err);
      clearSession();
      return null;
    }
  }, []);

  const clearSession = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('interaktia_token');
    localStorage.removeItem('interaktia_refresh_token');
  }, []);

  // Función para solicitar un nuevo Access Token usando el Refresh Token
  const refreshTokenAction = useCallback(async () => {
    if (isRefreshingRef.current) return null;
    isRefreshingRef.current = true;

    try {
      const savedRefreshToken = localStorage.getItem('interaktia_refresh_token');

      const res = await fetch(`${API_URL}v2/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ refreshToken: savedRefreshToken })
      });

      if (!res.ok) {
        clearSession();
        isRefreshingRef.current = false;
        return null;
      }

      const data = await res.json();
      const updatedUser = parseTokenAndSetSession(data.token, data.refreshToken);
      isRefreshingRef.current = false;
      return data.token;
    } catch (err) {
      console.error("Error al renovar token", err);
      clearSession();
      isRefreshingRef.current = false;
      return null;
    }
  }, [API_URL, clearSession, parseTokenAndSetSession]);

  // Al iniciar la app, verificar estado del token y renovar si es necesario
  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('interaktia_token');
      if (savedToken) {
        try {
          const decoded = jwtDecode(savedToken);
          const currentTime = Date.now() / 1000;
          
          // Si el token expira en menos de 5 minutos, renovarlo de una vez
          if (decoded.exp && decoded.exp - currentTime < 300) {
            await refreshTokenAction();
          } else {
            parseTokenAndSetSession(savedToken);
          }
        } catch (err) {
          await refreshTokenAction();
        }
      } else {
        // Intentar renovar por cookie HttpOnly si existe
        await refreshTokenAction();
      }
      setLoading(false);
    };

    initAuth();
  }, [parseTokenAndSetSession, refreshTokenAction]);

  // Timer para renovar automáticamente el token cada 15 minutos mientras haya usuario logueado
  useEffect(() => {
    if (!token) return;

    const interval = setInterval(() => {
      refreshTokenAction();
    }, 15 * 60 * 1000); // 15 minutos

    return () => clearInterval(interval);
  }, [token, refreshTokenAction]);

  const login = async (username, password) => {
    try {
      const res = await fetch(`${API_URL}v2/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password })
      });

      if (!res.ok) {
        const errData = await res.json();
        return { success: false, message: errData.message || 'Error al iniciar sesión' };
      }

      const data = await res.json();
      const sessionUser = parseTokenAndSetSession(data.token, data.refreshToken);
      return { success: true, user: sessionUser };
    } catch (err) {
      console.error("Login request error", err);
      return { success: false, message: 'Error de conexión con el servidor' };
    }
  };

  const logout = async () => {
    try {
      const currentToken = token;
      if (currentToken) {
        await fetch(`${API_URL}v2/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentToken}`
          },
          credentials: 'include'
        });
      }
    } catch (err) {
      console.error("Logout error", err);
    } finally {
      clearSession();
      window.location.href = '/login';
    }
  };

  // Fetch autenticado con reintento automático tras renovación silenciosa si recibe 401
  const authenticatedFetch = async (url, options = {}) => {
    let currentToken = token || localStorage.getItem('interaktia_token');

    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers = {
      ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers
    };

    if (currentToken) {
      headers['Authorization'] = `Bearer ${currentToken}`;
    }

    let response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers
    });

    // Si recibimos 401 Unauthorized, intentamos renovar el token y reintentar la petición
    if (response.status === 401) {
      const newToken = await refreshTokenAction();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, {
          ...options,
          credentials: 'include',
          headers
        });
      } else {
        clearSession();
        window.location.href = '/login';
      }
    }

    return response;
  };

  const value = {
    token,
    user,
    loading,
    login,
    logout,
    refreshToken: refreshTokenAction,
    authenticatedFetch
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
}
